'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  loadTemplates, saveTemplates, getActiveTemplateId, setActiveTemplateId,
  loadPrenotifTemplates, savePrenotifTemplates,
  getActivePrenotifTemplateId, setActivePrenotifTemplateId,
  loadCourierTemplates, saveCourierTemplates,
  getActiveCourierTemplateId, setActiveCourierTemplateId,
} from '@/lib/template-storage'
import { renderCourierTemplate, renderTemplateWithText, renderPrenotifTemplate } from '@/lib/template'
import type { TemplateData } from '@/lib/types'
import { TemplateSection } from '@/components/settings/TemplateSection'
import {
  CONFIRMATION_TEMPLATE_VARIABLES, COURIER_TEMPLATE_VARIABLES, PRENOTIF_TEMPLATE_VARIABLES,
} from '@/components/settings/template-variables'
import { CourierAutomationSettings } from '@/components/settings/CourierAutomationSettings'
import { NotificationIntegrationsSettings } from '@/components/settings/NotificationIntegrationsSettings'
import { ShippingOriginSettings } from '@/components/settings/ShippingOriginSettings'
import { MediaEdgeSettings } from '@/components/settings/MediaEdgeSettings'
import { ServiceLogSettings } from '@/components/settings/ServiceLogSettings'
import { ApiKeySettings } from '@/components/settings/ApiKeySettings'
import { StoreManagementSettings } from '@/components/settings/StoreManagementSettings'
import { OnecSyncSettings } from '@/components/settings/OnecSyncSettings'
import { CostEstimateSettings } from '@/components/settings/CostEstimateSettings'
import { ShowcaseFormulaSettings } from '@/components/settings/ShowcaseFormulaSettings'
import { SettingsSectionNavigation, type SettingsSection } from '@/components/settings/SettingsSectionNavigation'
import { ChannelKillSwitches } from '@/components/stock-guard/ChannelKillSwitches'
import { Switch } from '@/components/ui/Switch'
import { channelSettings, getStockGuardSettings, updateStockGuardSettings } from '@/lib/api/client/stock-guard'
import type { Marketplace, StockGuardSettings } from '@/lib/api/schemas/stock-guard.schema'

const SAMPLE_DATA: TemplateData = {
  recipientName: 'Иванова Ольга Ивановна',
  firstName: 'Ольга',
  shipDate: '15 апреля 2026',
  shipTime: '10:30',
  region: 'Московская область',
  cdekNumber: '1234567890',
  transitDays: '3',
  deliveryDate: '18 апреля 2026',
}

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const closeButton = useRef<HTMLButtonElement | null>(null)
  const drawerPanel = useRef<HTMLElement | null>(null)
  const rawSection = searchParams.get('section')
  const section: SettingsSection = rawSection === 'store-management'
    ? 'api-keys'
    : rawSection === 'templates' || rawSection === 'logs' || rawSection === 'api-keys' || rawSection === 'notifications' || rawSection === 'stock-sync' || rawSection === 'onec-sync' || rawSection === 'marketplace-prices' ? rawSection : 'notifications'

  useEffect(() => {
    if (!drawerOpen) return
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setDrawerOpen(false); return }
      if (event.key !== 'Tab' || !drawerPanel.current) return
      const focusable = Array.from(drawerPanel.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], select:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    window.addEventListener('keydown', onKeyDown)
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => closeButton.current?.focus())
    } else {
      closeButton.current?.focus()
    }
    return () => { window.removeEventListener('keydown', onKeyDown); previousFocus?.focus() }
  }, [drawerOpen])

  const selectSection = (next: SettingsSection) => {
    setSearchParams({ section: next })
    setDrawerOpen(false)
  }

  return (
    <div className="h-full overflow-y-auto bg-ivory">
      <div className="mx-auto max-w-6xl px-4 py-5 pb-[calc(24px+env(safe-area-inset-bottom))] sm:px-6 sm:py-8 md:px-10 md:py-10">
        <header className="mb-7 md:mb-10">
          <h1 className="text-[26px] md:text-[32px] font-semibold text-ink tracking-tight leading-none truncate">
            Настройки
          </h1>
          <div className="mt-5 h-px bg-rule" />
        </header>
        <button type="button" className="btn btn-ghost mb-5 w-full justify-between md:hidden" onClick={() => setDrawerOpen(true)} aria-haspopup="dialog" aria-controls="settings-section-drawer" aria-expanded={drawerOpen}>Разделы настроек <span aria-hidden="true">›</span></button>
        <div className="md:grid md:grid-cols-[220px_minmax(0,1fr)] md:items-start md:gap-8 lg:gap-12">
          <aside className="sticky top-6 hidden rounded-lg border border-rule bg-paper p-2 md:block"><SettingsSectionNavigation section={section} onSelect={selectSection} /></aside>
          <div className="min-w-0 max-w-3xl">
            {section === 'notifications' && <><MediaEdgeSettings /><ShippingOriginSettings /><CourierAutomationSettings /><NotificationIntegrationsSettings /></>}
            {section === 'templates' && <TemplatesSettings />}
            {section === 'logs' && <ServiceLogSettings />}
            {section === 'api-keys' && <><ApiKeySettings /><StoreManagementSettings /></>}
            {section === 'stock-sync' && <StockSyncSettings />}
            {section === 'onec-sync' && <><OnecSyncSettings /><CostEstimateSettings /></>}
            {section === 'marketplace-prices' && <ShowcaseFormulaSettings />}
          </div>
        </div>
      </div>
      {drawerOpen && <div id="settings-section-drawer" className="fixed inset-0 z-[80] md:hidden" role="dialog" aria-modal="true" aria-label="Разделы настроек"><button type="button" className="absolute inset-0 bg-onyx/35 backdrop-blur-[1px]" aria-label="Закрыть разделы настроек" onClick={() => setDrawerOpen(false)} /><aside ref={drawerPanel} className="absolute inset-y-0 left-0 w-[min(86vw,330px)] bg-paper pt-[env(safe-area-inset-top)] shadow-2xl"><div className="flex h-16 items-center justify-between border-b border-rule px-5"><h2 className="text-[15px] font-semibold text-ink">Разделы настроек</h2><button ref={closeButton} type="button" className="size-11 text-[25px] text-ink-muted" aria-label="Закрыть" onClick={() => setDrawerOpen(false)}>×</button></div><div className="p-3"><SettingsSectionNavigation section={section} onSelect={selectSection} /></div></aside></div>}
    </div>
  )
}

function StockSyncSettings() {
  const [settings, setSettings] = useState<StockGuardSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setSettings(await getStockGuardSettings())
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось загрузить синхронизацию остатков')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const changeChannel = async (marketplace: Marketplace, enabled: boolean) => {
    if (!settings || busy) return
    setBusy(true)
    setError(null)
    try {
      setSettings(await updateStockGuardSettings(channelSettings(settings.settings, marketplace, enabled)))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось изменить канал')
    } finally {
      setBusy(false)
    }
  }

  const changeRestore = async (marketplace: Marketplace, enabled: boolean) => {
    if (!settings || busy) return
    setBusy(true)
    setError(null)
    try {
      setSettings(await updateStockGuardSettings(
        marketplace === 'wildberries'
          ? { ...settings.settings, restoreOnWildberries: enabled }
          : { ...settings.settings, restoreOnYandex: enabled },
      ))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось изменить восстановление остатков')
    } finally {
      setBusy(false)
    }
  }

  const summary = settings?.planSummary

  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-gold/30 bg-paper p-4 text-[13px] leading-relaxed text-ink-2">
        <h2 className="text-[20px] font-semibold text-ink">Синхронизация остатков</h2>
        <p className="mt-1">Проданный или возвращаемый товар скрывается на витринах в течение одного цикла синхронизации.</p>
      </div>
      {error ? <p role="alert" className="rounded-md border border-st-unpaid/30 bg-st-unpaid/5 p-4 text-sm text-st-unpaid">{error}</p> : null}
      {loading ? <p aria-live="polite" className="rounded-lg border border-rule bg-paper p-5 text-sm text-ink-muted">Загружаем настройки синхронизации остатков…</p> : null}
      {!loading && settings ? (
        <>
          <div className="rounded-lg border border-rule bg-paper">
            <ChannelKillSwitches value={settings} canManage={true} busy={busy} onChange={changeChannel} />
            <div className="grid gap-3 border-t border-rule p-4 sm:grid-cols-2 sm:p-5">
              <RestoreSwitch
                marketplace="wildberries"
                enabled={settings.settings.restoreOnWildberries}
                busy={busy}
                onChange={changeRestore}
              />
              <RestoreSwitch
                marketplace="yandex-market"
                enabled={settings.settings.restoreOnYandex}
                busy={busy}
                onChange={changeRestore}
              />
            </div>
          </div>
          {summary ? <StockSyncPlanSummary summary={summary} /> : null}
        </>
      ) : null}
    </section>
  )
}

function RestoreSwitch({
  marketplace,
  enabled,
  busy,
  onChange,
}: {
  marketplace: Marketplace
  enabled: boolean
  busy: boolean
  onChange: (marketplace: Marketplace, enabled: boolean) => Promise<void>
}) {
  const label = marketplace === 'wildberries' ? 'Wildberries' : 'Яндекс Маркет'
  const switchLabel = `Возвращать в продажу, когда изделие снова на складе (${label})`
  return (
    <div className="rounded-md border border-rule bg-ivory/60 p-4">
      <div className="flex items-center justify-between gap-4">
        <p className="font-semibold text-ink">{switchLabel}</p>
        <Switch checked={enabled} label={switchLabel} disabled={busy} onChange={() => void onChange(marketplace, !enabled)} />
      </div>
      <p className="mt-2 text-xs text-ink-muted">Выключено по умолчанию: восстановление доступности требует отдельного решения владельца.</p>
    </div>
  )
}

function StockSyncPlanSummary({
  summary,
}: {
  summary: StockGuardSettings['planSummary']
}) {
  return (
    <section className="rounded-lg border border-rule bg-paper p-4 sm:p-5" aria-label="Последний план синхронизации">
      <h3 className="text-[15px] font-semibold text-ink">Последний план синхронизации</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {([
          ['ymarket', 'Яндекс Маркет'],
          ['wildberries', 'Wildberries'],
        ] as const).map(([channel, label]) => {
          const value = summary[channel]
          return (
            <div key={channel} className="rounded-md border border-rule bg-ivory/60 p-3 text-sm text-ink-2">
              <p className="font-semibold text-ink">{label}</p>
              <p className="mt-1">К 0: {value.writeZero} · К 1: {value.writeOne}</p>
              <p className="mt-1 text-xs text-ink-muted">Популяция: {value.population} · Не обработано: {value.notReached}{value.lastRunAt ? ` · ${new Date(value.lastRunAt).toLocaleString('ru-RU')}` : ' · запусков ещё не было'}</p>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function TemplatesSettings() {
  return <>
    <div className="mb-7 rounded-lg border border-gold/30 bg-paper p-4 text-[13px] leading-relaxed text-ink-2"><h2 className="text-[20px] font-semibold text-ink">Шаблоны сообщений</h2><p className="mt-1">Шаблоны хранятся только в этом браузере. Сохранение не меняет серверную автоматизацию и не отправляет сообщение.</p></div>
    <TemplateSection title="Шаблоны предуведомлений" subtitle="Первое сообщение до отправки посылки" loadFn={loadPrenotifTemplates} saveFn={savePrenotifTemplates} getActiveIdFn={getActivePrenotifTemplateId} setActiveIdFn={setActivePrenotifTemplateId} variables={PRENOTIF_TEMPLATE_VARIABLES} previewFn={(text) => renderPrenotifTemplate(text, 'Иван', '15.04.2026')} />
    <TemplateSection title="Шаблоны уведомления о курьере" subtitle="Постуведомление после назначения курьера СДЭК" loadFn={loadCourierTemplates} saveFn={saveCourierTemplates} getActiveIdFn={getActiveCourierTemplateId} setActiveIdFn={setActiveCourierTemplateId} variables={COURIER_TEMPLATE_VARIABLES} previewFn={(text) => renderCourierTemplate(text, { firstName: 'Иван', courierName: 'Алексей', courierPhone: '+7 999 123-45-67', courierCar: 'Lada Vesta', courierPlate: 'А123ВС', cdekNumber: '1234567890', confirmationCode: '482913' })} />
    <TemplateSection title="Шаблоны подтверждения отправки" subtitle="Сообщение с трек-номером и датой доставки" loadFn={loadTemplates} saveFn={saveTemplates} getActiveIdFn={getActiveTemplateId} setActiveIdFn={setActiveTemplateId} variables={CONFIRMATION_TEMPLATE_VARIABLES} previewFn={(text) => renderTemplateWithText(text, SAMPLE_DATA)} />
  </>
}
